import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { ModuleStructure } from "store/settings/slice";
import { useAppSelector, useAppDispatch } from "store/index";
import ROUTES from "router/routes";
import { logout } from "store/user/slice";
import { ModalTypes } from "configs/types";
import { openModal } from "store/modals/slice";
import {
  resolveSidebarItem,
  ResolvedSidebarItem,
} from "./sidebarMetadata";
import { isRouteActive, segmentToSlug } from "./sidebarPath";

import dashboardIcon from "assets/icons/sidebar/dashboard.svg";
import toggleIcon from "assets/icons/sidebar/sidebar-icon.svg";
import logoutIcon from "assets/icons/sidebar/logout.svg";
import plusIcon from "assets/icons/sidebar/plus.svg";
import minusIcon from "assets/icons/sidebar/minus.svg";
import arrowIcon from "assets/icons/sidebar/arrow-down.svg";
import { currentBrand } from "../../../configs/brands";
import SidebarSkeleton from "./SidebarSkeleton";
import Tooltip from "components/Tooltip";
import "./Sidebar.module.scss";

type GatewayNode = ModuleStructure | string[];

const hasChildNodes = (node: GatewayNode): boolean => {
  if (Array.isArray(node)) {
    return node.length > 0;
  }

  return Object.keys(node).length > 0;
};

const Sidebar: React.FC = () => {
  const [openSections, setOpenSections] = useState<string>("");
  const [openSubmenu, setOpenSubmenu] = useState<string>("");
  const [menuOpened, setMenuOpened] = useState<boolean>(true);

  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const isModulesFetched = useAppSelector(
    (store) => store.settings.isModulesFetched,
  );
  const email = useAppSelector((store) => store.auth.info?.email);
  const modules = useAppSelector((store) => store.settings.modules);
  const permissions = useAppSelector((store) => store.auth.info?.permissions);
  const isAdmin = useAppSelector(
    (store) => store.auth.info?.roleName === "admin",
  );
  const canReadBonusList =
    isAdmin || permissions?.live_ops_bonuses_list?.includes("read") === true;

  const isManualToggle = useRef(false);

  const pathParts = location.pathname.split("/").filter(Boolean);
  const activeSection = pathParts[0] ?? "";
  const activeSubmenu = pathParts[1] ?? "";
  const activeFinal = pathParts[2] ?? "";

  const toggleSection = (slug: string) => {
    isManualToggle.current = true;
    setOpenSections(slug);
    setOpenSubmenu("");
  };

  const toggleSubmenu = (slug: string) => {
    isManualToggle.current = true;
    setOpenSubmenu(slug);
  };

  const toggleMenu = () => {
    setMenuOpened(!menuOpened);
    if (menuOpened) {
      setOpenSubmenu("");
      setOpenSections("");
    }
  };

  useEffect(() => {
    if (isManualToggle.current) {
      isManualToggle.current = false;
      return;
    }

    const parts = location.pathname.split("/").filter(Boolean);
    setOpenSections(parts[0] ?? "");
    setOpenSubmenu(parts[1] ?? "");
  }, [location.pathname]);

  const handleLogout = () => {
    navigate(`${ROUTES.LOGIN}`);
    dispatch(logout());
  };

  const handleOpenPremiumModal = () => {
    navigate(`${location.pathname}?activeModal=${ModalTypes.premium}`);
    dispatch(openModal({ activeModal: ModalTypes.premium }));
  };

  const handleNavigate = (meta: ResolvedSidebarItem) => {
    if (meta.isPremium) {
      handleOpenPremiumModal();
      return;
    }

    navigate(meta.route);
  };

  const itemClassName = (
    baseClass: string,
    meta: ResolvedSidebarItem,
    isActive = false,
  ) =>
    [
      baseClass,
      isActive ? "active" : "",
      meta.isPremium ? "isPremium" : "",
      meta.isUnmapped ? "isUnmapped" : "",
    ]
      .filter(Boolean)
      .join(" ");

  const renderLeafButton = (
    meta: ResolvedSidebarItem,
    displayLabel: string,
    key: string,
  ) => {
    if (meta.key === "live_ops_bonuses_list" && !canReadBonusList) {
      return null;
    }

    return (
    <Tooltip
      key={key}
      text={displayLabel}
      position="right"
      delay={200}
      disabled={menuOpened}
      asChild
    >
      <button
        className={itemClassName(
          "menu-item menu-leaf",
          meta,
          isRouteActive(location.pathname, meta.route),
        )}
        onClick={(event) => {
          event.stopPropagation();
          handleNavigate(meta);
        }}
      >
        <div className="left-img">
          <img src={meta.icon} alt="" />
        </div>
        <span>{displayLabel}</span>
      </button>
    </Tooltip>
    );
  };

  const renderFinalMenu = (items: string[], parentPath: string[]) => {
    const parentSlug = segmentToSlug(parentPath[parentPath.length - 1] ?? "");

    return (
      <div
        className={`menu-final ${openSubmenu === parentSlug ? "opened" : ""}`}
      >
        {items.map((item) => {
          const pathSegments = [...parentPath, item];
          const meta = resolveSidebarItem(pathSegments);

          return renderLeafButton(meta, item, meta.key);
        })}
      </div>
    );
  };

  const renderSubmenuLevel = (node: GatewayNode, parentPath: string[]) => {
    const parentSlug = segmentToSlug(parentPath[parentPath.length - 1] ?? "");
    const isTopLevelSubmenu = parentPath.length === 1;
    const isSubmenuOpen = isTopLevelSubmenu
      ? openSections === parentSlug
      : openSubmenu === parentSlug;

    if (Array.isArray(node)) {
      return renderFinalMenu(node, parentPath);
    }

    if (node['Customized']) {
      const isBonusesAllowed =
        permissions?.cms_customized?.includes("read") === true;

      if (!isAdmin && !isBonusesAllowed) return null;
    }

    return (
      <div className={`menu-submenu ${isSubmenuOpen ? "opened" : ""}`}>
        {Object.entries(node).map(([name, childNode]) => {
          const pathSegments = [...parentPath, name];
          const meta = resolveSidebarItem(pathSegments);
          const slug = segmentToSlug(name);
          const isBranch = hasChildNodes(childNode);

          if (!isBranch) {
            return (
              <div key={meta.key} className="menu-section">
                <Tooltip
                  text={name}
                  position="right"
                  delay={200}
                  disabled={menuOpened}
                  asChild
                >
                  <button
                    className={itemClassName(
                      "menu-button menu-leaf",
                      meta,
                      isRouteActive(location.pathname, meta.route),
                    )}
                    onClick={(event) => {
                      event.stopPropagation();
                      handleNavigate(meta);
                    }}
                  >
                    <div className="left-img">
                      <img src={meta.icon} alt="" />
                    </div>
                    <span>{name}</span>
                    <div className="right-img empty">
                      <img src={arrowIcon} alt="" />
                    </div>
                  </button>
                </Tooltip>
              </div>
            );
          }

          if (Array.isArray(childNode)) {
            return (
              <div key={meta.key} className="menu-section">
                <Tooltip
                  text={name}
                  position="right"
                  delay={200}
                  disabled={menuOpened}
                  asChild
                >
                  <button
                    className={itemClassName(
                      "menu-button",
                      meta,
                      !activeFinal && activeSubmenu === slug,
                    )}
                    onClick={(event) => {
                      event.stopPropagation();
                      if (meta.isPremium) {
                        handleOpenPremiumModal();
                        return;
                      }
                      toggleSubmenu(openSubmenu === slug ? "" : slug);
                    }}
                  >
                    <div className="left-img">
                      <img src={meta.icon} alt="" />
                    </div>
                    <span>{name}</span>
                    <div className="right-img">
                      <img src={arrowIcon} alt="" />
                    </div>
                  </button>
                </Tooltip>
                {renderFinalMenu(childNode, pathSegments)}
              </div>
            );
          }

          return (
            <div key={meta.key} className="menu-section">
              <Tooltip
                text={name}
                position="right"
                delay={200}
                disabled={menuOpened}
                asChild
              >
                <button
                  className={itemClassName(
                    "menu-button",
                    meta,
                    !activeFinal && activeSubmenu === slug,
                  )}
                  onClick={(event) => {
                    event.stopPropagation();
                    if (meta.isPremium) {
                      handleOpenPremiumModal();
                      return;
                    }
                    toggleSubmenu(openSubmenu === slug ? "" : slug);
                  }}
                >
                  <div className="left-img">
                    <img src={meta.icon} alt="" />
                  </div>
                  <span>{name}</span>
                  <div className="right-img">
                    <img src={arrowIcon} alt="" />
                  </div>
                </button>
              </Tooltip>
              {renderSubmenuLevel(childNode, pathSegments)}
            </div>
          );
        })}
      </div>
    );
  };

  const renderTopLevelSection = (section: string, node: GatewayNode) => {
    const pathSegments = [section];
    const meta = resolveSidebarItem(pathSegments);
    const slug = segmentToSlug(section);
    const isBranch = hasChildNodes(node);

    if (!isBranch) {
      return (
        <div key={meta.key} className="menu-section">
          <Tooltip
            text={section}
            position="right"
            delay={200}
            disabled={menuOpened}
            asChild
          >
            <button
              className={itemClassName(
                "section-title",
                meta,
                isRouteActive(location.pathname, meta.route),
              )}
              onClick={(event) => {
                event.stopPropagation();
                handleNavigate(meta);
              }}
            >
              <div className="left-img">
                <img src={meta.icon} alt="" />
              </div>
              <span>{section}</span>
              <div className="right-img" />
            </button>
          </Tooltip>
        </div>
      );
    }

    return (
      <div key={meta.key} className="menu-section">
        <Tooltip
          text={section}
          position="right"
          delay={200}
          disabled={menuOpened}
          asChild
        >
          <button
            className={itemClassName(
              "section-title",
              meta,
              !activeSubmenu && !activeFinal && activeSection === slug,
            )}
            onClick={(event) => {
              event.stopPropagation();
              if (meta.isPremium) {
                handleOpenPremiumModal();
                return;
              }
              toggleSection(openSections === slug ? "" : slug);
            }}
          >
            <div className="left-img">
              <img src={meta.icon} alt="" />
            </div>
            <span>{section}</span>
            <div className="right-img">
              <img src={openSections === slug ? minusIcon : plusIcon} alt="" />
            </div>
          </button>
        </Tooltip>
        {renderSubmenuLevel(node, pathSegments)}
      </div>
    );
  };

  return (
    <div className={`sidebar-wrapper ${menuOpened ? "" : "closed"}`}>
      <div className="sidebar-header">
        {email ? (
          <div
            className={
              email === "vip-demo@prifinance.com" ? "logo logo-new" : "logo"
            }
            onClick={() => {
              setOpenSections("");
              setOpenSubmenu("");
              navigate(ROUTES.DASHBOARD);
            }}
          >
            <img
              src={
                menuOpened
                  ? currentBrand.logoUrl
                  : (currentBrand.logoMobUrl ?? currentBrand.logoUrl)
              }
              alt={currentBrand.title}
            />
          </div>
        ) : (
          <div className="logo-skeleton skeleton" />
        )}

        <div className="toggle-menu-btn" onClick={toggleMenu}>
          <img src={toggleIcon} alt="" />
        </div>
      </div>

      <div className="menu-wrapper">
        {!isModulesFetched ? (
          <SidebarSkeleton menuOpened={menuOpened} />
        ) : (
          modules && (
            <div
              className="menu"
              onClick={(event) => {
                event.stopPropagation();
                setMenuOpened(true);
              }}
            >
              <div className="menu-section">
                <Tooltip
                  text="Dashboard"
                  position="right"
                  delay={200}
                  disabled={menuOpened}
                  asChild
                >
                  <button
                    onClick={() => {
                      setOpenSections("");
                      setOpenSubmenu("");
                      navigate(ROUTES.DASHBOARD);
                    }}
                    className={`section-title dashboard ${location.pathname === ROUTES.DASHBOARD ? "active" : ""}`}
                  >
                    <div className="left-img">
                      <img src={dashboardIcon} alt="" />
                    </div>
                    <span>Dashboard</span>
                    <div className="right-img"></div>
                  </button>
                </Tooltip>
              </div>

              {Object.entries(modules).map(([section, submenu]) =>
                renderTopLevelSection(section, submenu),
              )}
            </div>
          )
        )}
      </div>

      {!isModulesFetched ? (
        <div className="logout-skeleton skeleton" />
      ) : (
        <Tooltip
          text={t("logOut")}
          position="right"
          delay={200}
          disabled={menuOpened}
          asChild
        >
          <div className="logout-btn" onClick={handleLogout}>
            <span>{t("logOut")}</span>
            <img src={logoutIcon} alt="" />
          </div>
        </Tooltip>
      )}
    </div>
  );
};

export default Sidebar;
